import { ArrowLeft, Heart } from '@phosphor-icons/react'
import { useCallback, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import '../discovery.css'

import { ErrorState } from '@/components/error-state'
import { LoadingState } from '@/components/loading-state'
import { KuniriLogo } from '@/components/kuniri-logo'

import { discoveryApi } from '../api'
import { ActivityGrid } from '../components/activity-grid'
import { DiscoveryNotice, type DiscoveryNoticeState } from '../components/discovery-notice'
import { EmptyState } from '../components/empty-state'
import { PullToRefresh } from '../components/pull-to-refresh'
import { RefreshButton } from '../components/refresh-button'
import { RefreshStatus } from '../components/refresh-status'
import { useActivityList } from '../hooks/use-activity-list'
import { useIsDesktop } from '../hooks/use-is-desktop'
import type { Activity } from '../types'

/** Real bookmarks destination (D-043), separate from the personal agenda. */
export function BookmarksScreen() {
  const { t } = useTranslation()
  const isDesktop = useIsDesktop()
  const { items, phase, refreshing, error, refresh, setItems } =
    useActivityList('saved')
  const [pendingIds, setPendingIds] = useState<Set<string>>(() => new Set())
  const [notice, setNotice] = useState<DiscoveryNoticeState | null>(null)

  const removeBookmark = useCallback(
    async (activity: Activity) => {
      if (pendingIds.has(activity.id)) return
      setPendingIds((current) => new Set(current).add(activity.id))
      setItems((current) => current.filter((item) => item.id !== activity.id))
      try {
        await discoveryApi.unsaveActivity(activity.id)
      } catch {
        setItems((current) =>
          current.some((item) => item.id === activity.id)
            ? current
            : [activity, ...current],
        )
        setNotice({ messageKey: 'discovery.notice.saveError', tone: 'error' })
      } finally {
        setPendingIds((current) => {
          const next = new Set(current)
          next.delete(activity.id)
          return next
        })
      }
    },
    [pendingIds, setItems],
  )

  return (
    <section aria-labelledby="bookmarks-title" className="content-width pb-10">
      <div className="flex items-center gap-2 py-4">
        <Link
          to="/saved"
          className="inline-flex min-h-11 items-center gap-1 rounded-full px-2 text-sm font-bold text-teal hover:bg-mint"
        >
          <ArrowLeft size={18} weight="bold" aria-hidden="true" />
          {t('screens.saved.title')}
        </Link>
        <Link to="/home" className="mx-auto inline-flex min-h-11 items-center">
          <KuniriLogo height={29} />
        </Link>
        <span className="w-24" aria-hidden="true" />
        {isDesktop ? (
          <RefreshButton onRefresh={refresh} refreshing={refreshing} />
        ) : null}
      </div>

      <h1 id="bookmarks-title" className="text-[clamp(2rem,4vw,3rem)]">
        {t('screens.bookmarks.title')}
      </h1>
      <p className="mt-2 max-w-2xl text-muted">{t('discovery.bookmarks.subtitle')}</p>

      <PullToRefresh
        className="mt-6"
        onRefresh={refresh}
        refreshing={refreshing}
        disabled={isDesktop}
      >
        <RefreshStatus visible={refreshing} />
        {phase === 'loading' ? (
          <LoadingState labelKey="discovery.bookmarks.loading" />
        ) : phase === 'error' ? (
          <ErrorState error={error} onRetry={refresh} />
        ) : items.length === 0 ? (
          <EmptyState
            icon={<Heart size={28} weight="bold" aria-hidden="true" />}
            titleKey="discovery.bookmarks.empty.title"
            bodyKey="discovery.bookmarks.empty.body"
            action={
              <Link to="/home" className="btn-secondary">
                {t('discovery.bookmarks.explore')}
              </Link>
            }
          />
        ) : (
          <ActivityGrid
            activities={items}
            onToggleSave={(activity) => void removeBookmark(activity)}
            pendingSaveIds={pendingIds}
          />
        )}
      </PullToRefresh>

      <DiscoveryNotice notice={notice} onDismiss={() => setNotice(null)} />
    </section>
  )
}
